import { Table, TableBody, TableCell, TableHead, TableRow } from '@aws-amplify/ui-react';
import type { Edulead } from 'edulead';
import { useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TokenPagination } from '../components/TokenPagination';
import LoadingBars from '../custom-components/LoadingBars';
import { GlobalContext } from '../global-context';
import { useTokenPager } from '../services/edulead-v4';
import { eduleadV4 } from '../services/edulead-v4-client';
import General from '../utils/GeneralUtils';

export function UnlockedInstitutions() {
  const globalContext = useContext(GlobalContext);
  const pageSize = 100;

  useEffect(() => {
    General.setTitle(document, 'Unlocked Accounts', globalContext);
  }, [globalContext]);

  // The team's unlocked institutions, newest first; errors are only logged, as before.
  const teamId = globalContext?.authInfo?.teamId;
  const pager = useTokenPager(
    (pageToken) => teamId
      ? eduleadV4.institutions.listUnlockedInstitutions({ page_size: pageSize, page_token: pageToken })
      : Promise.resolve({ data: [], response: {} as Edulead.EduleadV4ListUnlockedInstitutionsResponse }),
    [teamId]
  );
  const institutions = pager.items ?? [];
  const total = Number(pager.response?.total_size ?? 0);
  const isLoading = !teamId || pager.loading;

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="content content-800">
      <h2>Unlocked Accounts</h2>
      <p style={{ color: 'var(--amplify-colors-font-tertiary)', marginBottom: '24px' }}>
        Institutions that have been unlocked through contact exports. Total: {total.toLocaleString()}
      </p>

      {isLoading ? (
        <LoadingBars numberOfRows={5} />
      ) : institutions.length === 0 ? (
        <p style={{ color: 'var(--amplify-colors-font-tertiary)' }}>
          No institutions have been unlocked yet.
        </p>
      ) : (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell as="th">Institution ID</TableCell>
                <TableCell as="th">Unlocked At</TableCell>
                <TableCell as="th">Unlocked by Export</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {institutions.map((inst) => (
                <TableRow key={inst.id}>
                  <TableCell>
                    <Link to={`/directory/institutions/${inst.id}`}>
                      {inst.id}
                    </Link>
                  </TableCell>
                  <TableCell style={{ color: 'var(--amplify-colors-font-secondary)' }}>
                    {formatDate(inst.create_time ?? null)}
                  </TableCell>
                  <TableCell>
                    {inst.export_id ? (
                      <Link to={`/exports/${inst.export_id}`}>
                        View export
                      </Link>
                    ) : (
                      <span style={{ color: 'var(--amplify-colors-font-tertiary)' }}>-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <TokenPagination
            page={pager.page}
            pageSize={pageSize}
            shown={institutions.length}
            total={total}
            hasNext={pager.hasNext}
            isLoading={pager.loading}
            onPrevious={pager.prev}
            onNext={pager.next}
          />
        </>
      )}
    </div>
  );
}
